import * as React from 'react';
import { Box } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  Edge,
  NodeTypes,
  ReactFlow,
  Controls,
  useEdgesState,
  useNodesState,
  type OnSelectionChangeParams,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { getCalculatedNodesAndEdges } from './get-calculated-nodes-and-edges';
import { StartNode } from '../start-node/start-node.component';
import { ActionNode } from '../action-node/action-node.component';
import { DecisionNode } from '../decision-node/decision-node.component';
import { EndNode } from '../end-node/end-node.component';
import { Edge as CustomEdge } from '../edge/edge.component';
import type { FlowNode, LayoutNode } from '../../types';
import { EdgeMarker as CustomEdgeMarker } from '../edge/edge-marker.component';
import { useFlowContext } from '../../context/flow-context';
import { getLayoutedElements } from './get-layouted-elements';
import { NODE_TYPES } from './node-types.constant';

const nodeTypes: NodeTypes = {
  default: ActionNode,
  [NODE_TYPES.START]: StartNode,
  [NODE_TYPES.ACTION]: ActionNode,
  [NODE_TYPES.DECISION]: DecisionNode,
  [NODE_TYPES.END]: EndNode,
};

export const edgeTypes = {
  custom: CustomEdge,
};

export const FlowCanvas = ({ data }: { data?: Array<FlowNode> }) => {
  const intl = useIntl();
  const { nodeNextKeyMap, addStartNode, addEndNode } = useFlowContext();
  const edgesAndNodes = React.useMemo(
    () =>
      data
        ? getCalculatedNodesAndEdges(data, nodeNextKeyMap, {
            addStartNode,
            addEndNode,
          })
        : { nodes: [], edges: [] },
    [data, nodeNextKeyMap, addStartNode, addEndNode],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState([] as LayoutNode[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);

  // get the edges with translated labels
  const formattedEdges = React.useMemo(() => {
    const anyIntl: any = intl;
    const fm = anyIntl?.formatMessage || anyIntl?.formatHTMLMessage;
    return edgesAndNodes.edges.map((e) =>
      e.label && fm ? { ...e, label: fm({ id: String(e.label) }) } : e,
    );
  }, [edgesAndNodes.edges, intl]);

  React.useEffect(() => {
    const applyLayout = async () => {
      if (edgesAndNodes.nodes.length > 0 || formattedEdges.length > 0) {
        const { nodes: layoutedNodes, edges: layoutedEdges } =
          await getLayoutedElements(edgesAndNodes.nodes, formattedEdges);

        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
      }
    };

    applyLayout();
  }, [edgesAndNodes.nodes, formattedEdges, setNodes, setEdges]);

  return (
    <Box
      css={{
        height: '100%',
        width: '100%',
        border: '1px solid $onSurfaceBorderSubdued',
      }}
      data-testid="flow-canvas"
    >
      <ReactFlow
        edges={edges}
        nodes={nodes}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        minZoom={0.1}
        fitViewOptions={{ padding: 0.05 }}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onSelectionChange={(params: OnSelectionChangeParams) => {
          // When one or more edges are selected, move them to the end of the edges array
          // so they are rendered after (above) the other edges - the selected edge appears on top and will fully extend to the node is it connected to even if other edges are going into the same node. React Flow uses DOM order
          // for edge stacking not x-index.
          const selectedEdges = params?.edges ?? [];
          if (!selectedEdges.length) return;
          const selectedIds = new Set(selectedEdges.map((e) => e.id));
          setEdges((prev) => {
            const unselected = prev.filter((e) => !selectedIds.has(e.id));
            const selectedOrdered = prev.filter((e) => selectedIds.has(e.id));
            // Preserve relative order within each group.
            return [...unselected, ...selectedOrdered];
          });
        }}
      >
        <CustomEdgeMarker />
        <Controls />
      </ReactFlow>
    </Box>
  );
};
